import { useState } from 'react';
import { ASC, DESC, VALUATION_DATE_KEY } from 'common/constants/analyst-dashboards';
import { AttioSortDirection } from 'dashboard409a/common/constants/analyst-dashboard';
import { ModelsTable } from 'dashboard409a/components/ModelsTable/ModelsTable';
import { GenericWorkboard, useNoteHandler } from 'dashboard409a/components/ValuationsTable/common';
import { useModelsWithValuationData } from 'dashboard409a/hooks/useModelsWithValuationData';
import { CreateNoteRequest } from 'dashboard409a/services/attio.types';
import { getInitialSortFromStorage } from 'dashboard409a/utilities';
import { UserRole } from '../../../services/models.types';

const MODELS_SORT_KEY = 'modelsSort';

interface WorkboardModelsTableProps {
  // Filter type: 'current' for non-completed models, 'past' for completed models
  filterType?: 'current' | 'past';
  // Current user role for filtering
  currentUserRole?: UserRole;
}

const WorkboardModelsTable = ({
  filterType = 'current',
  currentUserRole = UserRole.REVIEWER,
}: WorkboardModelsTableProps) => {
  // Initialize state from localStorage if available
  const initialSort = getInitialSortFromStorage(MODELS_SORT_KEY, VALUATION_DATE_KEY, DESC);
  const [orderBy, setOrderBy] = useState(initialSort.sortBy);
  const [order, setOrder] = useState<AttioSortDirection>(initialSort.sortOrder);

  // Fetch models data with valuation data
  const {
    rows: models,
    isLoading,
    updateModelNote,
    updateModelStatus,
  } = useModelsWithValuationData({
    filterType,
    currentUserRole,
    orderBy,
    order,
  });

  // Use the generic note handler
  const { isNoteLoading, noteError, handleNoteSave: handleNoteSaveGeneric } = useNoteHandler({
    onSuccess: updateModelNote,
  });

  const handleNoteSave = async (fulfillment: number, noteContent: string, currentNote?: string) => {
    // Validate fulfillment_id
    if (!fulfillment) {
      throw new Error('Invalid fulfillment ID. Cannot save note.');
    }

    const request: CreateNoteRequest = {
      content: noteContent,
      fulfillment,
      reference_type: 'fu',
    };

    // Determine if we're updating an existing note or creating a new one
    const currentModel = models.find(m => m.id === fulfillment);
    const hasExistingNote = Boolean(
      (currentNote && currentNote.trim() !== '' && currentNote !== '-') ||
      (currentModel?.note && currentModel.note.content && currentModel.note.content.trim() !== '')
    );

    // Get the real note ID if we're updating
    const existingNoteId = currentModel?.note?.id?.toString();

    await handleNoteSaveGeneric(fulfillment, noteContent, currentNote, request, hasExistingNote, existingNoteId);
  };

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? DESC : ASC);
    setOrderBy(property);
  };

  const handleStatusUpdate = async (modelId: number, newStatus: number) => {
    await updateModelStatus(modelId, newStatus);
  };

  return (
    <GenericWorkboard>
      <ModelsTable
        isLoading={isLoading}
        rows={models}
        onNoteSave={handleNoteSave}
        orderBy={orderBy}
        order={order}
        onRequestSort={handleRequestSort}
        onStatusUpdate={handleStatusUpdate}
        isNoteLoading={isNoteLoading}
        noteError={noteError}
      />
    </GenericWorkboard>
  );
};

export default WorkboardModelsTable;
